// frontend/src/hooks/useNotifications.js
import { useState, useEffect, useCallback, useRef } from 'react';
import API from '../services/api';

const FALLBACK_POLL_INTERVAL = 30000; // 30s fallback if SSE fails

export default function useNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount]     = useState(0);
  const esRef      = useRef(null);
  const pollRef    = useRef(null);
  const mountedRef = useRef(true);

  const fetchAll = useCallback(async () => {
    try {
      const [listRes, countRes] = await Promise.all([
        API.get('/notifications', { params: { limit: 60 } }),
        API.get('/notifications/count'),
      ]);
      if (!mountedRef.current) return;
      if (listRes.data.success)  setNotifications(listRes.data.notifications);
      if (countRes.data.success) setUnreadCount(countRes.data.count);
    } catch {
      // silencieux — 401 géré par l'intercepteur API
    }
  }, []);

  // ── SSE setup ──────────────────────────────────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;

    // Initial fetch
    fetchAll();

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const baseURL = (API.defaults.baseURL || '').replace(/\/$/, '');

    if (token && typeof EventSource !== 'undefined') {
      const url = `${baseURL}/notifications/stream?token=${encodeURIComponent(token)}`;
      const es  = new EventSource(url);
      esRef.current = es;

      es.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === 'refresh') fetchAll();
          // 'connected' and 'ping' are ignored
        } catch {}
      };

      es.onerror = () => {
        // SSE failed — close and fall back to polling
        es.close();
        esRef.current = null;
        if (mountedRef.current && !pollRef.current) {
          pollRef.current = setInterval(fetchAll, FALLBACK_POLL_INTERVAL);
        }
      };
    } else {
      // No SSE support — use polling
      pollRef.current = setInterval(fetchAll, FALLBACK_POLL_INTERVAL);
    }

    return () => {
      mountedRef.current = false;
      esRef.current?.close();
      esRef.current = null;
      clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [fetchAll]);

  // ── Actions ────────────────────────────────────────────────────────────────
  const markRead = useCallback(async (id) => {
    try {
      await API.put(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: 1 } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await API.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
      setUnreadCount(0);
    } catch {}
  }, []);

  const remove = useCallback(async (id) => {
    const notif = notifications.find(n => n.id === id);
    try {
      await API.delete(`/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
      if (notif && !notif.is_read) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  }, [notifications]);

  return { notifications, unreadCount, markRead, markAllRead, remove, refresh: fetchAll };
}
